/**
 * GDPR Consent helper za Google Analytics 4 (Consent Mode v2).
 *
 * Workflow:
 *  1. index.html postavlja gtag('consent', 'default', { ...denied, wait_for_update: 500 })
 *     PRIJE gtag('config', ...). To znači: GA4 NE šalje hit dok ne dobije
 *     consent update u prvih 500ms.
 *  2. Pri app mount-u, applyStoredConsent() čita localStorage i ako je
 *     korisnik ranije pristao -> odmah grant (unutar 500ms wait window-a).
 *  3. Pri klik "Prihvati" / "Odbij" u CookieConsent banner-u, eksplicitno
 *     grant/deny je potvrđen (i van 500ms window-a, GA prima updated state).
 *
 * Bez ovoga, GA4 šalje hit prije nego što korisnik vidi banner -> GDPR violation.
 */

export const CONSENT_KEY = 'suton-consent';

export type ConsentStatus = 'accepted' | 'declined' | null;

type GtagFn = (
  command: 'consent',
  action: 'default' | 'update',
  params: Record<string, string | number>
) => void;

interface WindowWithGtag {
  gtag?: GtagFn;
}

const GRANTED_PARAMS: Record<string, string> = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
};

const DENIED_PARAMS: Record<string, string> = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

function getGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & WindowWithGtag;
  return typeof w.gtag === 'function' ? w.gtag : null;
}

export function grantAnalyticsConsent(): void {
  const gtag = getGtag();
  if (!gtag) return;
  try {
    gtag('consent', 'update', GRANTED_PARAMS);
  } catch {
    /* noop — Consent Mode call failed, GA will stay denied */
  }
}

export function denyAnalyticsConsent(): void {
  const gtag = getGtag();
  if (!gtag) return;
  try {
    gtag('consent', 'update', DENIED_PARAMS);
  } catch {
    /* noop */
  }
}

/**
 * Pri app mount-u: ako je consent već persistiran u localStorage, propagiraj
 * ga u gtag. Pokreće se prije isteka `wait_for_update: 500ms` window-a iz
 * index.html, pa GA4 dobija pravi state PRIJE prvog hit-a.
 */
export function applyStoredConsent(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  let stored: ConsentStatus = null;
  try {
    stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus;
  } catch {
    return;
  }
  if (stored === 'accepted') {
    grantAnalyticsConsent();
  } else if (stored === 'declined') {
    denyAnalyticsConsent();
  }
}

export const CONSENT_RESET_EVENT = 'suton-consent-reset';

/**
 * Reset cookie consent — koristi se iz Settings ekrana ("Cookie-Einstellungen").
 *
 * Workflow:
 *  1. Briše localStorage 'suton-consent' kako bi banner ponovo bio prikazan.
 *  2. Vraća GA4 u 'denied' state (povratak na default iz index.html).
 *  3. Dispatch-uje custom DOM event 'suton-consent-reset' na window-u —
 *     CookieConsent komponenta ga sluša i ponovo prikaže banner BEZ reload-a
 *     (audio reprodukcija se ne prekida).
 */
export function resetConsent(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* noop */
  }
  denyAnalyticsConsent();
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_RESET_EVENT));
  } catch {
    /* noop */
  }
}
